import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import PbxContext from '../../../context/PbxContext';

import {
  accessLocalStorage,
  valuesAnalyze,
  changeViewForms,
  readInterfaces,
  stageIpChange,
  confirmIpChange,
  rollbackIpChange,
  ipChangeHealth,
} from '../../../services';
import FormIPSet from './formIpSetInterfaces';

import '../../../libs/bulma.min.css';
import './ChangeIp.css';

// Troca de IP com commit-confirm (Gap 2 do plano).
// 3 estados:
//   1. 'edit'     — formulário padrão, botão "Aplicar".
//   2. 'staging'  — countdown 120 s; botões "Confirmar" (após 10 s) e "Desfazer".
//   3. 'done'     — mostra resultado final.
// Se o navegador perder conexão no estado 'staging', o servidor reverte sozinho
// via systemd-run (--on-active=120s) e a UI, ao recarregar, mostra o IP antigo.
function ChangeIp() {
  const getItensStateGlobal = useContext(PbxContext);
  const { toggleIsHidden } = getItensStateGlobal;

  const history = useHistory();

  const [phase, setPhase] = useState('edit');             // 'edit' | 'staging' | 'done'
  const [doneKind, setDoneKind] = useState(null);         // 'confirmed' | 'rolled_back' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [stageInfo, setStageInfo] = useState({ token: null, deadline: null, applied: null });
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [confirmUnlockSec, setConfirmUnlockSec] = useState(10);

  const [values, setValues] = useState({});
  const [validate, setValidate] = useState({});
  const [disable, setDisable] = useState(true);
  const [interfaces, setInterfaces] = useState([]);
  const [fullDataIfaces, setFullDataIfaces] = useState({});

  const countdownTimer = useRef(null);
  const unlockTimer = useRef(null);

  const { changeBorderImputToRedOrNormal, changeVisibityMessages } = changeViewForms;

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user } = dataUser;
    if (!user || user.role !== 'root' || user.idtenant !== 'vgs') {
      return history.push('/home');
    }
    const getNetworkData = await readInterfaces();
    const filterIfaces = getNetworkData.map((element) => {
      delete element['lo'];
      return element;
    });
    setInterfaces(Object.keys(...filterIfaces));
    Object.keys(...filterIfaces).forEach((i) => setValues((prev) => ({ ...prev, [i]: { iface: i } })));
    setFullDataIfaces(...filterIfaces);

    // Verifica se há um staging pendente (ex.: o operador recarregou a página).
    try {
      const health = await ipChangeHealth();
      if (health.status === 200 && health.data && health.data.stagePending) {
        setPhase('staging');
        setStageInfo({ token: null, deadline: health.data.deadline, applied: null });
        startCountdown(new Date(health.data.deadline).getTime());
      }
    } catch (_) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const changeScreen = (check, name) => {
    changeBorderImputToRedOrNormal(check, name);
    changeVisibityMessages(check, `${name}-invalid-data`);
  };

  const handleChangeDataAnalyze = (name, value) => {
    const { isIP, isMask, checkNumber } = valuesAnalyze;
    const itemCheck = name.split('-')[1];
    let ok = false;
    if (itemCheck === 'ip' || itemCheck === 'gw') ok = isIP(value);
    if (itemCheck === 'mask') ok = isMask(value);
    if (itemCheck === 'metric') ok = checkNumber(value);
    changeScreen(ok, name);
    return ok;
  };

  const handleChangeValues = (item) => {
    const { name, value } = item;
    const element = name.split('-');
    const interfaceName = element[0];
    const prop = element[1];
    const ok = handleChangeDataAnalyze(name, value);
    setValidate((prev) => ({ ...prev, [name]: ok }));
    setValues((prev) => ({
      ...prev,
      [interfaceName]: { ...(prev[interfaceName] || {}), iface: interfaceName, [prop]: value },
    }));
    // Habilita o botão só quando houver pelo menos 4 campos preenchidos e nenhum inválido.
    const keys = Object.keys({ ...validate, [name]: ok });
    const hasInvalid = keys.some((k) => ({ ...validate, [name]: ok })[k] === false);
    setDisable(keys.length < 4 || hasInvalid);
  };

  const startCountdown = (deadlineMs) => {
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
    setConfirmUnlockSec(10);
    unlockTimer.current = setInterval(() => {
      setConfirmUnlockSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const tick = () => {
      const left = Math.max(0, Math.round((deadlineMs - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        clearInterval(countdownTimer.current);
        // Servidor reverteu sozinho; refresca UI.
        setTimeout(async () => {
          try {
            const h = await ipChangeHealth();
            if (h.status === 200 && !h.data.stagePending) {
              setPhase('done');
              setDoneKind('rolled_back');
            }
          } catch (_) {}
        }, 1500);
      }
    };
    tick();
    countdownTimer.current = setInterval(tick, 1000);
  };

  useEffect(() => () => {
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
  }, []);

  const applyChange = async () => {
    // Para esta iteração, enviamos a primeira interface preenchida
    // (backend commit-confirm só processa uma por vez).
    const payload = Object.values(values).filter((iface) => Object.values(iface).length > 1);
    if (payload.length === 0) return;
    setErrorMsg('');
    setWarnings([]);
    const resp = await stageIpChange(payload);
    if (resp.status !== 202) {
      setErrorMsg((resp.data && resp.data.message) || 'Falha ao aplicar.');
      return;
    }
    setStageInfo({
      token: resp.data.token,
      deadline: resp.data.deadline,
      applied: resp.data.applied,
    });
    setWarnings(resp.data.warnings || []);
    setPhase('staging');
    startCountdown(new Date(resp.data.deadline).getTime());
  };

  const doConfirm = async () => {
    if (!stageInfo.token) {
      setErrorMsg('Sessão expirada — sem token para confirmar.');
      return;
    }
    const resp = await confirmIpChange(stageInfo.token);
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
    if (resp.status === 200) {
      setPhase('done');
      setDoneKind('confirmed');
    } else {
      setErrorMsg((resp.data && resp.data.message) || 'Falha ao confirmar.');
    }
  };

  const doRollback = async () => {
    if (!stageInfo.token) {
      setErrorMsg('Sessão expirada — sem token para reverter.');
      return;
    }
    const resp = await rollbackIpChange(stageInfo.token);
    clearInterval(countdownTimer.current);
    clearInterval(unlockTimer.current);
    if (resp.status === 200) {
      setPhase('done');
      setDoneKind('rolled_back');
    } else {
      setErrorMsg((resp.data && resp.data.message) || 'Falha ao desfazer.');
    }
  };

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  return (
    <div className="change-ip-container">
      <div className="change-ip-header">
        <div className="change-ip-title-wrapper">
          <svg className="change-ip-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div>
            <h1 className="change-ip-title">Alterar IP</h1>
            <p className="change-ip-subtitle">Commit-confirm com rollback automático em 2 minutos</p>
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="alert-message alert-danger">
          <div className="alert-body"><strong>Erro:</strong> {errorMsg}</div>
        </div>
      )}

      {phase === 'edit' && (
        <div className="change-ip-content">
          <div className="alert-message alert-info">
            <div className="alert-body">
              <strong>Atenção:</strong> Após aplicar você tem <strong>2 minutos</strong> para confirmar.
              Se perder o acesso (ex.: configuração incorreta), o servidor voltará sozinho ao IP anterior.
            </div>
          </div>
          <div id="change-ip-div" className="interfaces-list">
            {interfaces.map((iface) => (
              <FormIPSet
                key={iface}
                iface={iface}
                data={fullDataIfaces[iface]}
                handleChangeValues={handleChangeValues}
              />
            ))}
          </div>
          <div className="change-ip-actions">
            <button className="btn-submit-ip" type="submit" disabled={disable} onClick={applyChange}>
              <span>Aplicar (com confirmação)</span>
            </button>
          </div>
        </div>
      )}

      {phase === 'staging' && (
        <div className="change-ip-content">
          <div className="alert-message alert-warning">
            <div className="alert-body">
              <strong>Mudança aplicada em modo de teste.</strong><br />
              Tempo restante para confirmar: <strong>{secondsLeft}s</strong><br />
              {stageInfo.applied && (
                <>Interface <code>{stageInfo.applied.iface}</code>, novo IP <code>{stageInfo.applied.newIp}/{stageInfo.applied.newPrefix}</code>{stageInfo.applied.newGw && <> via <code>{stageInfo.applied.newGw}</code></>}.</>
              )}
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="alert-message alert-warning">
              <div className="alert-body">
                <strong>Avisos de pré-validação:</strong>
                <ul>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            </div>
          )}
          <div className="change-ip-actions">
            <button
              className="btn-submit-ip"
              onClick={doConfirm}
              disabled={confirmUnlockSec > 0}
              title={confirmUnlockSec > 0 ? `Aguarde ${confirmUnlockSec}s` : 'Confirmar a mudança'}
            >
              <span>{confirmUnlockSec > 0 ? `Confirmar (em ${confirmUnlockSec}s)` : 'Confirmar'}</span>
            </button>
            <button className="btn-cancel-ip" onClick={doRollback}>
              <span>Desfazer agora</span>
            </button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div className="change-ip-content">
          {doneKind === 'confirmed' && (
            <div className="alert-message alert-success">
              <div className="alert-body">Troca de IP <strong>confirmada</strong>.</div>
            </div>
          )}
          {doneKind === 'rolled_back' && (
            <div className="alert-message alert-warning">
              <div className="alert-body">Mudança foi <strong>revertida</strong> — servidor está no IP anterior.</div>
            </div>
          )}
          <div className="change-ip-actions">
            <button className="btn-submit-ip" onClick={() => window.location.reload()}>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangeIp;
